// ================================================================
// SafetyWorklistPage — suicide-safety event worklist (Mind)
// Review pathway steps 3-4: trained clinician acknowledges, completes
// the Brief Suicide Safety Assessment, documents disposition, resolves.
// DB guard enforces: no resolution without acknowledgement + BSSA +
// disposition. Escalation clocks run server-side until acknowledged.
// ================================================================
import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const DISPOSITIONS = [
  ['no_further_action', 'No further action indicated'],
  ['safety_plan_outpatient', 'Safety plan + outpatient follow-up'],
  ['urgent_outpatient_referral', 'Urgent outpatient referral'],
  ['emergency_evaluation', 'Emergency evaluation'],
  ['emergency_services_activated', 'Emergency services activated'],
]
const BSSA_FIELDS = [
  ['ideation_frequency', 'Frequency and duration of ideation'],
  ['plan', 'Plan (specificity, if any)'],
  ['means_access', 'Access to means'],
  ['past_behavior', 'Past suicidal behavior'],
  ['protective_factors', 'Protective factors and supports'],
  ['clinical_summary', 'Clinical summary and rationale'],
]

const card = { background: '#0d1526', border: '1px solid #1f2b45', borderRadius: 10, padding: 20, marginBottom: 16 }
const h = { color: '#c9a96e', fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 12px' }
const inp = { width: '100%', background: '#060e1c', border: '1px solid #374151', borderRadius: 6, padding: '8px 10px', color: '#fff', fontSize: 14, fontFamily: 'inherit' }
const btn = { background: '#c9a96e', color: '#060e1c', border: 'none', borderRadius: 8, padding: '9px 20px', fontWeight: 700, cursor: 'pointer' }

export default function SafetyWorklistPage() {
  const [events, setEvents] = useState([])
  const [open, setOpen] = useState(null)     // event being worked
  const [bssa, setBssa] = useState({})
  const [dispo, setDispo] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const load = async () => {
    const { data } = await supabase.schema('clinical').from('assessment_safety_events')
      .select('*').is('resolved_at', null).order('created_at', { ascending: false })
    setEvents(data ?? [])
  }
  useEffect(() => { load() }, [])

  const acknowledge = async (ev) => {
    setBusy(true); setErr('')
    const { data: u } = await supabase.auth.getUser()
    const { error } = await supabase.schema('clinical').from('assessment_safety_events')
      .update({ acknowledged_at: new Date().toISOString(), acknowledged_by: u?.user?.id ?? null, status: 'acknowledged' })
      .eq('safety_event_id', ev.safety_event_id)
    setBusy(false)
    if (error) { setErr(error.message); return }
    await load()
  }

  const resolve = async (ev) => {
    setBusy(true); setErr('')
    const filled = BSSA_FIELDS.every(([k]) => (bssa[k] ?? '').trim().length > 0)
    if (!filled || !dispo) { setBusy(false); setErr('All BSSA fields and a disposition are required before resolution.'); return }
    const { error } = await supabase.schema('clinical').from('assessment_safety_events')
      .update({
        bssa: { ...bssa, completed_at: new Date().toISOString(), instrument: 'Brief Suicide Safety Assessment' },
        disposition: dispo,
        resolution_note: `BSSA completed; disposition: ${dispo}`,
        resolved_at: new Date().toISOString(),
        status: 'resolved',
      })
      .eq('safety_event_id', ev.safety_event_id)
    setBusy(false)
    if (error) { setErr(error.message); return }
    setOpen(null); setBssa({}); setDispo('')
    await load()
  }

  const clock = (ev) => {
    if (ev.acknowledged_at) return 'acknowledged'
    const mins = Math.round((new Date(ev.ack_deadline_at) - Date.now()) / 60000)
    return mins >= 0 ? `${mins} min to deadline` : `OVERDUE ${-mins} min · escalation level ${ev.escalation_level}`
  }

  return (
    <div style={{ padding: 24, maxWidth: 860 }}>
      <h2 style={{ color: '#e5e7eb', fontFamily: 'Rajdhani, sans-serif', margin: '0 0 4px' }}>Safety Worklist</h2>
      <div style={{ color: '#8a9bc0', fontSize: 13, marginBottom: 18 }}>
        Positive safety screens require acknowledgement, a Brief Suicide Safety Assessment by a
        trained clinician, and a documented disposition before resolution. Escalation timers run
        until acknowledged.
      </div>
      {err && <div style={{ color: '#ff8a8a', marginBottom: 12 }}>{err}</div>}
      {events.length === 0 && <div style={{ ...card, color: '#8a9bc0' }}>No open safety events.</div>}
      {events.map(ev => (
        <div key={ev.safety_event_id} style={{ ...card, borderColor: ev.severity === 'emergent' ? '#ff8a8a' : '#c9a96e' }}>
          <h3 style={h}>
            {ev.severity?.toUpperCase()} · patient #{ev.patient_id} · {new Date(ev.created_at).toLocaleString()}
          </h3>
          <div style={{ color: '#e5e7eb', fontSize: 14, marginBottom: 6 }}>{ev.trigger_reference}</div>
          <div style={{ color: ev.acknowledged_at ? '#8a9bc0' : '#ff8a8a', fontSize: 13, marginBottom: 10 }}>{clock(ev)}</div>

          {!ev.acknowledged_at && (
            <button style={btn} disabled={busy} onClick={() => acknowledge(ev)}>Acknowledge</button>
          )}

          {ev.acknowledged_at && open !== ev.safety_event_id && (
            <button style={btn} disabled={busy} onClick={() => { setOpen(ev.safety_event_id); setBssa({}); setDispo('') }}>
              Document BSSA & Disposition
            </button>
          )}

          {open === ev.safety_event_id && (
            <div style={{ marginTop: 14 }}>
              <h3 style={h}>Brief Suicide Safety Assessment</h3>
              {BSSA_FIELDS.map(([k, label]) => (
                <div key={k} style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 12, color: '#c9a96e', marginBottom: 4 }}>{label}</label>
                  <textarea rows={2} style={inp} value={bssa[k] ?? ''}
                    onChange={e => setBssa(prev => ({ ...prev, [k]: e.target.value }))} />
                </div>
              ))}
              <label style={{ display: 'block', fontSize: 12, color: '#c9a96e', marginBottom: 4 }}>Disposition</label>
              <select style={{ ...inp, marginBottom: 12 }} value={dispo} onChange={e => setDispo(e.target.value)}>
                <option value="">Select disposition…</option>
                {DISPOSITIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={btn} disabled={busy} onClick={() => resolve(ev)}>{busy ? 'Saving…' : 'Resolve Event'}</button>
                <button style={{ ...btn, background: 'transparent', color: '#c9a96e', border: '1px solid #c9a96e' }}
                  onClick={() => setOpen(null)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
